---
id: libs-features-doc
title: Features
owner: Platform Team
status: Active
lastReviewed: 2026-01-27
visibility: public
tags:
  - feature
  - shared
  - internal
---

## Product Overview

This package supplies the shared feature experiences used across Rezi apps,
covering resume creation, editing, previews, and related workflow tooling.

### Primary Workflow
App shells compose these feature modules to assemble end-to-end resume
workflows such as building, previewing, and downloading documents across
consumer and enterprise surfaces.

### Business Logic
Feature modules encapsulate reusable product behavior for resume generation,
editing, preview presentation, settings, and supporting actions so that
multiple apps expose consistent user experiences.

### Outcomes
The package enables shared feature delivery across apps, reducing duplication
and keeping core resume experiences aligned.

### Out of Scope
Routing, application state bootstrapping, and backend services are managed by
the apps that consume these features.

## Technical Overview

Features are organized by subfolders that map to product areas (generator and
editor flows, preview surfaces, resume tools, and supporting UI helpers). Each
area encapsulates its feature-level components and hooks, and is composed by
app shells to deliver end-to-end flows.

### Entry Points
Features are imported from `@rezi/features/*` with many subfolders exporting
index entry points, such as `libs/features/src/editor`, `libs/features/src/preview`,
`libs/features/src/resume-builder`, and `libs/features/src/keyword-tool`.

### Key Modules
Core resume creation and editing lives in `libs/features/src/editor`,
`libs/features/src/resume-builder`, and `libs/features/src/generator-*`, while
preview and presentation modules live under `libs/features/src/preview`,
`libs/features/src/preview-template`, and `libs/features/src/template-selector`.

### Data and Services
Feature modules rely on shared stores and utilities from `@rezi/stores` and
`@rezi/utils`, and expect data and session state to be provided by the hosting
apps.

### Integrations and Side Effects
Modules such as `libs/features/src/stripe` and `libs/features/src/ai-usage-counter`
coordinate billing and usage visibility, while navigation and layout helpers in
`libs/features/src/nav-bar` and `libs/features/src/hoc/layout` plug into app
shells.

### Constraints and Risks
Because these features are shared across apps, changes can affect multiple
surfaces and require cross-app validation.

## Changelog

- 2026-01-27: Expanded product and technical overviews to cover business logic and key code paths.
- 2026-01-22: Updated overview content to reflect usage and folder context.
- 2026-01-21: Replaced `audience` with `visibility` in frontmatter.
